import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {Alert, Button} from '@betareader/design-system';
import styled from 'styled-components';
import {
  useCritiqueGroupPreview,
  useRequestToJoin,
  useWithdrawJoinRequest,
} from 'src/hooks/useCritiqueGroups';
import { BookSpine } from 'src/components/critiqueGroups/bookSpine';
import {
  CritiqueErrorState,
  CritiqueLoadingState,
} from 'src/components/critiqueGroups/critiqueStates';
import { visibilityLabel } from 'src/utils/critiqueGroupUtil';

/**
 * The public metadata view of a critique group for a NON-MEMBER — everything
 * they may see before being admitted, plus the "Request to join" affordance.
 *
 * Rendered by critiqueGroupDetail when the member endpoint 403s but a preview
 * is available. It reads ONLY the public projection (useCritiqueGroupPreview);
 * it must never touch member-only data, which is why the projection is a
 * separate, flatter type.
 */

const Wrap = styled.div`
  max-width: 620px;
  margin: 0 auto;
`;

const Hero = styled.div`
  background: #123c3a;
  color: #fff;
  border-radius: 16px;
  padding: 26px 26px 22px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 800;
`;

const Badge = styled.span`
  font-size: 11px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.16);
  padding: 4px 10px;
  border-radius: 999px;
  margin-left: 10px;
  white-space: nowrap;
`;

const Sub = styled.p`
  margin: 6px 0 0;
  font-size: 13.5px;
  color: #b8d6d2;
`;

const Stat = styled.div`
  margin-top: 16px;
  font-size: 13px;
  color: #cfe6e2;
`;

const Card = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 14px;
  padding: 22px 24px;
  margin-top: 18px;
`;

const CritiqueGroupPreview: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams<{ groupId: string }>();

  const { data: group, isLoading, isError, error, refetch } =
    useCritiqueGroupPreview(groupId);
  const requestToJoin = useRequestToJoin(groupId);
  const withdraw = useWithdrawJoinRequest(groupId);

  if (isLoading) return <CritiqueLoadingState />;
  if (isError || !group) {
    return (
      <CritiqueErrorState
        message={
          error?.status === 404
            ? t('CritiqueNoAccess', 'This group isn’t available to you.')
            : error?.message
        }
        onRetry={() => refetch()}
      />
    );
  }

  const badge = visibilityLabel(group.visibility, t);
  const status = group.myRequestStatus || 'none';

  return (
    <Wrap>
      <Hero>
        {group.book && (
          <BookSpine
            title={group.book.title}
            coverUrl={group.book.coverUrl || undefined}
          />
        )}
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <Title>{group.name || t('CritiqueUntitledGroup', 'Untitled group')}</Title>
            <Badge data-testid="preview-visibility-badge">
              {badge.label}
            </Badge>
          </div>
          {group.host?.displayName && (
            <Sub>
              {t('CritiqueHostedBy', 'Hosted by {{name}}', {
                name: group.host.displayName,
              })}
            </Sub>
          )}
          {group.book && (
            <Sub>
              {t('CritiqueReading', 'Reading: {{title}}', {
                title: group.book.title,
              })}
            </Sub>
          )}
          <Stat>
            {t('CritiqueMemberCount', '{{count}} members', {
              count: group.memberCount,
            })}
            {group.memberCap != null && ` / ${group.memberCap}`}
          </Stat>
        </div>
      </Hero>

      <Card>
        {status === 'member' && (
          <Alert variant='success' data-testid="preview-already-member">
            <strong>
              {t('CritiqueAlreadyMember', 'You’re already in this group')}
            </strong>
            <p>
              <Button
                variant='secondary'
                style={{ marginTop: 8, borderRadius: 9 }}
                onClick={() => history.push('/critique-groups')}
              >
                {t('CritiqueGoToMyGroups', 'Go to my groups')}
              </Button>
            </p>
          </Alert>
        )}

        {status === 'pending' && (
          <div data-testid="preview-pending">
            <Alert>
              <strong>
                {t('CritiqueRequestPending', 'Your request is pending')}
              </strong>
              <p>
                {t(
                  'CritiqueRequestPendingBody',
                  'The host has been notified. You’ll get access once they approve you.'
                )}
              </p>
            </Alert>
            <Button
              variant='danger'
              aria-busy={withdraw.isPending}
              disabled={withdraw.isPending}
              onClick={() => withdraw.mutate()}
              style={{ borderRadius: 9, fontWeight: 700 }}
              data-testid="preview-withdraw"
            >
              {t('CritiqueWithdrawRequest', 'Withdraw request')}
            </Button>
          </div>
        )}

        {status === 'declined' && (
          <div data-testid="preview-declined">
            <Alert variant='warning'>
              <strong>
                {t('CritiqueRequestDeclined', 'Your request was declined')}
              </strong>
              <p>
                {t(
                  'CritiqueRequestDeclinedBody',
                  'The host declined your request to join this group. You can’t request again right now.'
                )}
              </p>
            </Alert>
          </div>
        )}

        {status === 'none' && (
          <div data-testid="preview-join">
            <p style={{ fontSize: 14, color: '#6b675e', marginBottom: 14 }}>
              {group.isFull
                ? t(
                    'CritiqueGroupFullBody',
                    'This group is currently full. You can’t request to join right now.'
                  )
                : t(
                    'CritiqueRequestToJoinBody',
                    'Ask to join this critique group. The host reviews every request — you won’t get access to the book until they approve you.'
                  )}
            </p>
            <Button
              disabled={group.isFull || requestToJoin.isPending}
              aria-busy={requestToJoin.isPending}
              onClick={() => requestToJoin.mutate()}
              style={{ borderRadius: 9, fontWeight: 700 }}
              data-testid="preview-request-join"
            >
              {group.isFull
                ? t('CritiqueGroupFull', 'Group is full')
                : t('CritiqueRequestToJoin', 'Request to join')}
            </Button>
            {requestToJoin.isError && (
              <Alert variant='error' style={{ marginTop: 12 }}>
                {requestToJoin.error?.message}
              </Alert>
            )}
          </div>
        )}
      </Card>
    </Wrap>
  );
};

export default CritiqueGroupPreview;
